import {
  LayoutDashboard,
  Users,
  Calendar,
  Shield,
  Settings,
  AlertCircle,
  FileText,
  TrendingUp,
  LogOut,
  ChevronLeft,
  Wrench,
  Bot,
} from 'lucide-react';
import React, { useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';

import { useAuth } from './Auth/AuthContext';
import './AdminLayout.css';

const AdminLayout = () => {
  const navigate = useNavigate();
  const { logout, user, profile, isAdmin, loading } = useAuth();

  // Security check: redirect non-admins
  useEffect(() => {
    if (!loading && !isAdmin) {
      console.warn('🚫 Non-admin user attempted to access admin panel');
      navigate('/home', { replace: true });
    }
  }, [isAdmin, loading, navigate]);

  // Show loading state while checking auth
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: '#0a0a0a',
        }}
      >
        <div style={{ color: '#fff' }}>Loading...</div>
      </div>
    );
  }

  // Don't render admin UI if not admin
  if (!isAdmin) {
    return null;
  }

  const menuItems = [
    {
      path: '/admin',
      label: 'Dashboard',
      icon: LayoutDashboard,
      exact: true,
    },
    {
      path: '/host',
      label: 'Host Dashboard',
      icon: Bot,
    },
    {
      path: '/admin/users',
      label: 'User Management',
      icon: Users,
    },
    {
      path: '/admin/events',
      label: 'Event Management',
      icon: Calendar,
    },
    {
      path: '/admin/host-tools',
      label: 'Host Tools',
      icon: Wrench,
    },
    {
      path: '/admin/reports',
      label: 'Reports & Flags',
      icon: AlertCircle,
    },
    {
      path: '/admin/content',
      label: 'Content Management',
      icon: FileText,
    },
    {
      path: '/admin/revenue',
      label: 'Revenue & Fees',
      icon: TrendingUp,
    },
    {
      path: '/admin/security',
      label: 'Security',
      icon: Shield,
    },
    {
      path: '/admin/settings',
      label: 'Settings',
      icon: Settings,
    },
  ];

  const handleLogout = async () => {
    // Properly logout using auth context
    await logout();
    navigate('/login');
  };

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div className="admin-sidebar-header">
          <div className="admin-logo">
            <Shield className="admin-logo-icon" />
            <span className="admin-logo-text">Admin Panel</span>
          </div>
          <button onClick={() => navigate('/home')} className="admin-back-btn">
            <ChevronLeft size={20} />
            Back to Site
          </button>
        </div>

        <nav className="admin-nav">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.exact}
                className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="admin-sidebar-footer">
          <div className="admin-user-info">
            <div className="admin-user-avatar">
              {profile?.display_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'A'}
            </div>
            <div className="admin-user-details">
              <div className="admin-user-name">
                {profile?.display_name || user?.email?.split('@')[0] || 'Admin User'}
              </div>
              <div className="admin-user-role">Administrator</div>
            </div>
          </div>
          <button onClick={handleLogout} className="admin-logout-btn">
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </aside>

      <main className="admin-main">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
